<template>
  <li class="result-item h-auto" data-test-id="search-result-item">
    <a
      class="flex w-full flex-1 items-center gap-4 px-6 py-2 text-base transition-colors duration-100 ease-in-out hover:bg-black-10"
      :href="to"
      @click.prevent="handleClick(index, to, term)"
    >
      <NuxtImg
        v-if="imageUrl"
        class="aspect-[3/4] h-16 w-auto bg-gray-40 lg:h-9"
        sizes="xs:96px, lg:27px"
        :src="imageUrl"
      ></NuxtImg>
      <WordHighlighter :query="term" highlight-class="font-bold bg-transparent">
        <slot />
      </WordHighlighter>
    </a>
  </li>
</template>

<script lang="ts">
import WordHighlighter from 'vue-word-highlighter'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'
import useSearchResults from '#layers/base/composables/useSearchResults'

export default defineComponent({
  name: 'SearchResultItem',
  components: {
    WordHighlighter,
  },
  props: {
    term: {
      type: String as PropType<string>,
      default: '',
    },
    to: {
      type: [Object, String] as PropType<string>,
      required: true,
    },
    imageUrl: {
      type: String as PropType<string>,
      default: '',
    },
    index: { type: Number as PropType<number>, default: () => {} },
    section: { type: String as PropType<string>, default: () => {} },
  },
  emits: ['click:result'],

  setup(props, { emit }) {
    const { trackSearchSuggestions, trackSearch } = useTrackingEvents()
    const { products, categories } = useSearchResults()
    const router = useRouter()
    const handleClick = (
      position: number,
      suggestion: string,
      searchTerm: string,
    ) => {
      trackSearchSuggestions({
        searchTerm,
        suggestion,
        position: position + 1,
        section: props.section,
      })

      const isCategory = props.section === 'category'
      const suggestedCategories = categories.value.map(
        (c: any) => c.name || c.path || '',
      )
      const suggestedProducts = products.value.map((p: any) =>
        String(p.id || ''),
      )

      trackSearch({
        searchTerm: suggestion,
        originalSearchTerm: searchTerm,
        originalSearchTermLength: searchTerm.length,
        searchAction: isCategory ? 'category_click' : 'product_click',
        searchType: isCategory ? 'category' : 'product',
        searchSuggestedCategories: suggestedCategories,
        searchSuggestedProducts: suggestedProducts,
        numSearchSuggestions:
          suggestedCategories.length + suggestedProducts.length,
        searchDestination: props.to as string,
      })

      if (import.meta.client) {
        sessionStorage.setItem('search_result', 'true')
      }

      emit('click:result')
      router.push(props.to as string)
    }
    return {
      handleClick,
    }
  },
})
</script>

<style scoped>
.result-item mark {
  background: transparent !important;
  font-weight: bold !important;
}
</style>
